import { FormEvent, useEffect, useState } from "react";
import { api } from "../../../api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { ColorPicker } from "../../../components/ui/color-picker";
import { useLanguage } from "../../../contexts/LanguageContext";
import { employeesLabels } from "../labels";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { UserCog, X, Loader2, Mail, Lock, Palette, Users, Save, User } from "lucide-react";
import { toast } from "sonner";
import { cn } from "../../../lib/utils";
import type { Professional } from "../../../types";

interface EditEmployeeModalProps {
  professional: Professional | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditEmployeeModal({ professional, isOpen, onClose, onSuccess }: EditEmployeeModalProps) {
  const { lang } = useLanguage();
  const tPage = employeesLabels[lang].page;
  const tForm = employeesLabels[lang].newModal;
  
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [color, setColor] = useState<string>("#71B27B");
  const [capacity, setCapacity] = useState("1");
  const [canToggleAdmin, setCanToggleAdmin] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [confirmSelfDemote, setConfirmSelfDemote] = useState(false);
  const [me, setMe] = useState<{ id: number; is_admin: boolean } | null>(null);

  useEffect(() => {
    if (isOpen && professional) {
      setName(professional.name);
      setEmail(professional.email);
      setPassword("");
      setIsAdmin(Boolean(professional.is_admin));
      setColor(professional.color || "#71B27B");
      setCapacity(String(professional.capacity ?? 1));
      setError(null);
      
      const loadMe = async () => {
        const meRes = await api.me();
        if (meRes.ok) {
          setMe({ id: meRes.data.id, is_admin: Boolean(meRes.data.is_admin) });
          setCanToggleAdmin(Boolean(meRes.data.is_admin));
        }
      };
      loadMe();
    }
  }, [isOpen, professional]);

  if (!isOpen) return null;

  const handleSave = async () => {
    if (!professional) return;
    setSaving(true);
    setError(null);
    const parsedCapacity = Number.parseInt(capacity, 10);
    const normalizedCapacity = Number.isFinite(parsedCapacity) && parsedCapacity > 0 ? parsedCapacity : 1;
    
    const res = await api.updateProfessional(professional.uid || professional.id, {
      name: name.trim(),
      email: email.trim(),
      ...(password ? { password } : {}),
      ...(canToggleAdmin ? { is_admin: isAdmin } : {}),
      color,
      capacity: normalizedCapacity,
    });
    setSaving(false);

    if (!res.ok) {
      setError(res.error);
      toast.error(res.error || (lang === 'pt' ? "Erro ao atualizar profissional" : "Error updating professional"));
      return;
    }

    toast.success(lang === 'pt' ? "Profissional atualizado com sucesso!" : "Professional updated successfully!");
    onSuccess();
    onClose();
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!professional) return;
    if (!name.trim() || !email.trim()) {
      toast.error(tForm.toasts.fillRequired);
      return;
    }

    if (
      me &&
      professional.id === me.id &&
      professional.is_admin &&
      !isAdmin
    ) {
      setConfirmSelfDemote(true);
      return;
    }
    
    void handleSave();
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-neutral-900/40 backdrop-blur-sm animate-in fade-in duration-200">
        <div 
          className="bg-white w-full max-w-lg rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-8 py-6 border-b border-neutral-50 bg-neutral-50/30 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                <UserCog className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-neutral-800 tracking-tight">{tPage.editTitle}</h2>
                <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest">{lang === 'pt' ? 'Editar informações do colaborador' : 'Edit collaborator information'}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onClose}
              className="rounded-xl hover:bg-neutral-100 transition-all active:scale-90"
            >
              <X className="w-5 h-5 text-neutral-400" />
            </Button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="p-8 space-y-6">
              <div className="space-y-1">
                <Label htmlFor="edit-employee-name" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                  {tForm.fields.name}
                </Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                    <User className="w-4 h-4" />
                  </div>
                  <Input
                    id="edit-employee-name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    required
                    placeholder={tForm.fields.namePlaceholder}
                    className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                  />
                </div>
              </div>
              
              <div className="space-y-1">
                <Label htmlFor="edit-employee-email" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                  {tForm.fields.email}
                </Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <Input
                    id="edit-employee-email"
                    type="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    placeholder={tForm.fields.emailPlaceholder}
                    className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                  />
                </div>
              </div>

              <div className="space-y-1">
                <Label htmlFor="edit-employee-password" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                  {tForm.fields.password} <span className="lowercase font-medium text-neutral-300">({lang === 'pt' ? 'deixe em branco para não alterar' : 'leave blank to keep current'})</span>
                </Label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                    <Lock className="w-4 h-4" />
                  </div>
                  <Input
                    id="edit-employee-password"
                    type="password"
                    minLength={6}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder={tForm.fields.passwordPlaceholder}
                    className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="space-y-1">
                  <Label htmlFor="edit-employee-color" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                    {tForm.fields.color}
                  </Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                      <Palette className="w-4 h-4" />
                    </div>
                    <div className="pl-11 h-12 flex items-center bg-neutral-50/30 border border-neutral-100 rounded-2xl">
                      <ColorPicker id="edit-employee-color" value={color} onChange={setColor} />
                    </div>
                  </div>
                </div>

                <div className="space-y-1">
                  <Label htmlFor="edit-employee-capacity" className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest ml-1 mb-1.5 block">
                    {tForm.fields.capacity}
                  </Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-neutral-400">
                      <Users className="w-4 h-4" />
                    </div>
                    <Input
                      id="edit-employee-capacity"
                      type="number"
                      min={1}
                      step={1}
                      value={capacity}
                      onChange={(event) => setCapacity(event.target.value)}
                      className="pl-11 h-12 rounded-2xl border-neutral-100 bg-neutral-50/30 focus-visible:ring-emerald-500/20 font-medium"
                    />
                  </div>
                </div>
              </div>

              {canToggleAdmin && (
                <div 
                  className="p-4 rounded-2xl bg-neutral-50/50 border border-neutral-100 flex items-center justify-between cursor-pointer group hover:bg-white transition-all"
                  onClick={() => setIsAdmin(!isAdmin)}
                >
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-xl flex items-center justify-center transition-all",
                      isAdmin ? "bg-emerald-100 text-emerald-600" : "bg-neutral-100 text-neutral-400"
                    )}>
                      <Users className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-neutral-800 tracking-tight">{tForm.fields.admin}</p>
                      <p className="text-[10px] font-medium text-neutral-400 uppercase tracking-widest">
                        {isAdmin ? (lang === 'pt' ? 'Acesso total ao sistema' : 'Full system access') : (lang === 'pt' ? 'Acesso restrito' : 'Restricted access')}
                      </p>
                    </div>
                  </div>
                  <div className={cn(
                    "w-11 h-6 rounded-full relative transition-all duration-200 ease-in-out",
                    isAdmin ? "bg-emerald-500" : "bg-neutral-200"
                  )}>
                    <div className={cn(
                      "absolute top-1 left-1 bg-white w-4 h-4 rounded-full transition-transform duration-200 ease-in-out shadow-sm",
                      isAdmin ? "translate-x-5" : "translate-x-0"
                    )} />
                  </div>
                </div>
              )}
              
              {error && (
                <div className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-600 text-xs font-bold uppercase tracking-wider text-center">
                  {error}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-8 py-6 border-t border-neutral-50 bg-neutral-50/30 flex items-center justify-end gap-4">
              <Button 
                type="button" 
                variant="ghost" 
                onClick={onClose}
                className="h-12 px-8 font-bold text-neutral-400 rounded-2xl hover:bg-neutral-100 transition-all active:scale-95"
              >
                {tPage.buttons.cancel}
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="h-12 px-12 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-2xl transition-all active:scale-[0.98] shadow-none flex items-center gap-2"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {saving ? lang === 'pt' ? 'Salvando...' : 'Saving...' : tPage.buttons.save}
              </Button>
            </div>
          </form>
        </div>
      </div>

      <AlertDialog open={confirmSelfDemote} onOpenChange={setConfirmSelfDemote}>
        <AlertDialogContent className="rounded-3xl border-0 shadow-2xl">
          <AlertDialogHeader className="flex flex-col gap-2 text-left">
            <AlertDialogTitle className="text-left font-bold text-neutral-800 tracking-tight">{tPage.confirmSelfDemote.title}</AlertDialogTitle>
            <AlertDialogDescription className="text-left text-neutral-500 font-medium">
              {tPage.confirmSelfDemote.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="pt-4 gap-3">
            <AlertDialogCancel className="h-11 rounded-xl font-bold border-neutral-200">{tPage.buttons.keep}</AlertDialogCancel>
            <AlertDialogAction
              className="h-11 rounded-xl font-bold bg-red-600 hover:bg-red-700 text-white border-none"
              onClick={() => {
                setConfirmSelfDemote(false);
                void handleSave();
              }}
            >
              {tPage.buttons.remove}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
